import { supabase } from '@/integrations/supabase/client';
import { ClienteSubdivisaoRegra } from '@/types/clienteSubdivisao';
import { convertHoursToDecimal } from '@/utils/ordensServico/ordensServicoTimeUtils';

interface OrdemServicoParaAnalise {
  empresa_nome: string;
  empresa_id?: number;
  operador_nome?: string;
  operador_id?: number;
  horas_totais: string;
  projeto_fechado?: boolean;
  api_data?: any;
}

interface ResultadoDesmembramento {
  clienteNome: string;
  clienteId: number;
  valorHora: number | null;
  regraAplicada: boolean;
}

// Cache para regras para evitar múltiplas consultas
const regrasCache = new Map<number, ClienteSubdivisaoRegra[]>();

// Função para converter dados do banco para a estrutura da regra
const convertDatabaseToRegra = (item: any): ClienteSubdivisaoRegra => ({
  ...item,
  operador_ids: item.operador_ids ? (Array.isArray(item.operador_ids) ? item.operador_ids : []) : null,
});

/** Valor/hora vigente do cliente no cadastro (`clientes_valores_historico`) para a data da OS. */
export const buscarValorHoraClienteFilho = async (clienteFilhoId: number, dataOS: string): Promise<number | null> => {
  try {
    // Buscar o cliente filho
    const { data: clienteFilho, error: clienteError } = await supabase
      .from('clientes_cadastro')
      .select('id, nome')
      .eq('empresa_id', clienteFilhoId)
      .maybeSingle();

    if (clienteError || !clienteFilho) {
      console.warn(`Cliente filho não encontrado no cadastro: ID ${clienteFilhoId}`);
      return null;
    }

    // Buscar valor histórico válido para a data da OS
    const { data: valoresHistorico, error: valorError } = await supabase
      .from('clientes_valores_historico')
      .select('valor_hora, data_reajuste, id')
      .eq('cliente_id', clienteFilho.id)
      .lte('data_reajuste', dataOS)
      .order('id', { ascending: false })
      .limit(1);

    if (valorError) {
      console.error('Erro ao buscar valor histórico do cliente filho:', valorError);
      return null;
    }

    const valorHistorico = valoresHistorico?.[0];
    if (valorHistorico && valorHistorico.valor_hora !== null && valorHistorico.valor_hora !== undefined && valorHistorico.valor_hora > 0) {
      const valor = Number(valorHistorico.valor_hora);
      console.log(`✅ Valor/hora do cliente filho ${clienteFilho.nome}: R$ ${valor}/hora (data: ${valorHistorico.data_reajuste}, ID: ${valorHistorico.id})`);
      return valor;
    }

    console.warn(`Cliente filho ${clienteFilho.nome} não possui valor/hora válido no histórico`);
    return null;
  } catch (error) {
    console.error('Erro ao buscar valor/hora do cliente filho:', error);
    return null;
  }
};

/**
 * Valor/hora para faturamento após regra de desmembramento:
 * 1) `cliente_subdivisao_regras.valor_hora` (cadastro da regra)
 * 2) senão histórico do cliente filho (`clientes_valores_historico`).
 */
export async function resolveValorHoraAposRegra(
  regra: Pick<ClienteSubdivisaoRegra, 'valor_hora' | 'cliente_filho_id'>,
  dataOS: string
): Promise<number | null> {
  const ymd = String(dataOS).slice(0, 10);
  const fromRegra =
    regra.valor_hora != null && regra.valor_hora !== undefined && Number(regra.valor_hora) > 0
      ? Number(regra.valor_hora)
      : null;
  if (fromRegra != null) return Math.round(fromRegra * 100) / 100;
  return buscarValorHoraClienteFilho(regra.cliente_filho_id, ymd);
}

export const aplicarRegrasDesmembramentoAnalise = async (
  ordem: OrdemServicoParaAnalise,
  valorHoraPadrao: number = 0
): Promise<ResultadoDesmembramento> => {
  // Resultado padrão (sem regra aplicada)
  const resultadoPadrao: ResultadoDesmembramento = {
    clienteNome: ordem.empresa_nome || 'Cliente não informado',
    clienteId: ordem.empresa_id || 0,
    valorHora: null, // null indica que deve usar valor histórico
    regraAplicada: false
  };

  // Se não temos ID do cliente, não podemos aplicar regras
  if (!ordem.empresa_id) {
    return resultadoPadrao;
  }

  try {
    // Buscar regras do cache ou do banco
    let regras = regrasCache.get(ordem.empresa_id);
    
    if (!regras) {
      const { data, error } = await supabase
        .from('cliente_subdivisao_regras')
        .select('*')
        .eq('cliente_pai_id', ordem.empresa_id)
        .eq('ativo', true)
        .order('prioridade', { ascending: true });

      if (error) {
        console.error('Erro ao buscar regras de desmembramento:', error);
        return resultadoPadrao;
      }

      regras = (data || []).map(convertDatabaseToRegra);
      regrasCache.set(ordem.empresa_id, regras);
    }

    if (regras.length === 0) {
      return resultadoPadrao;
    }

    // Converter horas para decimal para comparação
    const horasTotaisDecimal = convertHoursToDecimal(ordem.horas_totais || '00:00:00');

    // Determinar tipo de projeto - verificar também projeto_fechado diretamente
    const tipoProjetoOS = ordem.projeto_fechado === true ? 'FECHADO' : 'ABERTO';

    console.log(`🔍 VERIFICANDO REGRAS para OS - Cliente: ${ordem.empresa_nome}, Operador: ${ordem.operador_nome}, Horas: ${horasTotaisDecimal}, Tipo: ${tipoProjetoOS}`);

    // Verificar cada regra por ordem de prioridade
    for (const regra of regras) {
      console.log(`📋 Testando regra ID ${regra.id} - Cliente filho: ${regra.cliente_filho_nome} (ID: ${regra.cliente_filho_id})`);
      
      // REGRA 1: Se operador está definido na regra
      if (regra.operador_ids && regra.operador_ids.length > 0) {
        if (ordem.operador_id && regra.operador_ids.includes(ordem.operador_id)) {
          console.log(`✅ REGRA APLICADA por operador - Regra ID: ${regra.id}`);
          
          // Buscar valor/hora do cadastro do cliente filho
          const dataOS = ordem.api_data?.data?.real || new Date().toISOString().split('T')[0];
          const valorHoraClienteFilho = await resolveValorHoraAposRegra(regra, String(dataOS));

          return {
            clienteNome: regra.cliente_filho_nome || `Cliente ${regra.cliente_filho_id}`,
            clienteId: regra.cliente_filho_id,
            valorHora: valorHoraClienteFilho,
            regraAplicada: true,
          };
        } else {
          console.log(`❌ Operador ${ordem.operador_id} não está na lista [${regra.operador_ids.join(', ')}]`);
          continue;
        }
      }

      // REGRA 2: Operador vazio → aplicar lógica condicional

      // Verificar produto (se definido na regra)
      if (regra.produto) {
        // Tentar extrair produto da API data ou usar lógica existente
        const produtoOS = ordem.api_data?.produto?.nome || '';
        const produtoNormalizado = produtoOS.toUpperCase().replace(/\s+/g, '_');
        
        if (produtoNormalizado !== regra.produto) {
          console.log(`❌ Produto ${produtoNormalizado} não confere com ${regra.produto}`);
          continue;
        }
      }

      // Verificar tipo de projeto
      if (tipoProjetoOS !== regra.tipo_projeto) {
        console.log(`❌ Tipo projeto ${tipoProjetoOS} não confere com ${regra.tipo_projeto}`);
        continue;
      }

      // Verificar condição de horas
      const condicaoAtendida = regra.operador_logico === '>' 
        ? horasTotaisDecimal > regra.quantidade_horas_limite
        : horasTotaisDecimal < regra.quantidade_horas_limite;

      if (!condicaoAtendida) {
        console.log(`❌ Condição de horas não atendida: ${horasTotaisDecimal} ${regra.operador_logico} ${regra.quantidade_horas_limite}`);
        continue;
      }

      // Todas as condições atendidas - aplicar regra!
      console.log(`✅ REGRA APLICADA por condições - Regra ID: ${regra.id}`);
      
      // Buscar valor/hora do cadastro do cliente filho
      const dataOS = ordem.api_data?.data?.real || new Date().toISOString().split('T')[0];
      const valorHoraClienteFilho = await resolveValorHoraAposRegra(regra, String(dataOS));

      return {
        clienteNome: regra.cliente_filho_nome || `Cliente ${regra.cliente_filho_id}`,
        clienteId: regra.cliente_filho_id,
        valorHora: valorHoraClienteFilho,
        regraAplicada: true,
      };
    }

    console.log(`❌ NENHUMA REGRA APLICADA para cliente ${ordem.empresa_nome}`);
    return resultadoPadrao;

  } catch (error) {
    console.error('Erro ao aplicar regras de desmembramento:', error);
    return resultadoPadrao;
  }
};

// Função para limpar cache quando necessário
export const limparCacheRegras = () => {
  regrasCache.clear();
};
